import { useState } from "react";
import { formatOutlookQty } from "@/lib/manufacturing/inventoryOutlookFormat";
import { openManufacturingPartUse } from "@/lib/workspaceModeNavigation";
import { orderMappingTagKey, orderMappingTagLabel, type OrderMappingTag } from "@/lib/manufacturing/orderMappingBulk";
import type { InventoryOutlookReport } from "@/lib/manufacturing/inventoryMatchClient";

const inputClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900";

type Gap = NonNullable<InventoryOutlookReport["gaps"]>[number];

const FIELD_LABEL: Record<string, string> = {
  width: "Width",
  length: "Length",
  series: "Series",
  roof_style: "Roof style",
  package: "Package",
  roof_type: "Roof type",
  roof_color: "Roof color",
  siding_type: "Siding type",
  siding_color: "Siding color",
  trim_color: "Trim color",
  addon_name: "Add-on name",
};

const REASON_LABEL: Record<Gap["reason"], string> = {
  assumed: "default SKU",
  no_match: "no member for that finish",
  ambiguous: "two finishes tied",
};

export function InventoryOutlookOrderMappingRow({
  gap,
  picked,
  open,
  channel,
  busy,
  selectedTags,
  checked,
  members,
  materialId,
  matchReason,
  onTogglePicked,
  onOpen,
  onAddTag,
  onToggleChecked,
  onMaterial,
  onSave,
}: {
  gap: Gap;
  picked: boolean;
  open: boolean;
  channel: string;
  busy: boolean;
  selectedTags: readonly OrderMappingTag[];
  checked: string[];
  members: Array<{ material_id: string; material_code: string; material_name: string }>;
  materialId: string;
  matchReason: string;
  onTogglePicked: () => void;
  onOpen: () => void;
  onAddTag: (tag: OrderMappingTag) => void;
  onToggleChecked: (id: string) => void;
  onMaterial: (materialId: string) => void;
  onSave: () => void;
}) {
  const [confirmLeave, setConfirmLeave] = useState(false);
  const selectedKeys = new Set(selectedTags.map(orderMappingTagKey));
  const bulk = selectedTags.length > 0;
  const remaining = gap.aspects.filter((row) => !selectedKeys.has(orderMappingTagKey(row)));
  return (
    <li className="rounded-xl border border-gray-200 p-3 text-sm dark:border-zinc-700">
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={picked}
          aria-label={`Select ${gap.serial} ${gap.use_code}`}
          onChange={onTogglePicked}
        />
        <button type="button" className="flex-1 text-left" onClick={onOpen}>
          <span className="font-semibold">{gap.serial}</span>
          {" · "}
          {gap.use_code}
          {" · "}
          {channel || "no channel"}
          {" · "}
          {gap.material_code || gap.material_name}
          {" · "}
          {formatOutlookQty(gap.quantity)}
          {" · "}
          {REASON_LABEL[gap.reason]}
        </button>
      </div>
      {open ? (
        <div className="mt-3 space-y-2">
          {confirmLeave ? (
            <div className="space-y-2 rounded-md border border-gray-200 p-2 dark:border-zinc-700">
              <p>
                Leave order mapping and open {gap.use_code} in Models, on Part uses? Unsaved tag filters and matches on this screen will be dropped.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white dark:bg-zinc-100 dark:text-zinc-900"
                  onClick={() => openManufacturingPartUse(gap.material_use_id)}
                >
                  Leave and edit
                </button>
                <button type="button" className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600" onClick={() => setConfirmLeave(false)}>
                  Stay
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="text-sm text-gray-600 underline dark:text-zinc-400" onClick={() => setConfirmLeave(true)}>
              Edit channel
            </button>
          )}
          {gap.aspects.length === 0 ? (
            <p className="text-gray-500 dark:text-zinc-400">This order has no facts to match on. Write the rule under Map order indicators.</p>
          ) : null}
          {bulk ? (
            <div className="flex flex-wrap gap-2">
              {remaining.map((row) => (
                <button
                  key={orderMappingTagKey(row)}
                  type="button"
                  className="rounded-md border border-gray-200 px-2 py-1 dark:border-zinc-700"
                  onClick={() => onAddTag(row)}
                >
                  {orderMappingTagLabel(row)}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {gap.aspects.map((row) => {
                const id = orderMappingTagKey(row);
                return (
                  <label key={id} className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 dark:border-zinc-700">
                    <input type="checkbox" checked={checked.includes(id)} onChange={() => onToggleChecked(id)} />
                    {FIELD_LABEL[row.field] ?? row.field} {row.value}
                  </label>
                );
              })}
            </div>
          )}
          {matchReason ? <p className="text-gray-600 dark:text-zinc-400">{matchReason}</p> : null}
          <div className="flex flex-wrap items-center gap-2">
            <select className={inputClass} value={materialId} aria-label={`SKU for ${gap.use_code}`} onChange={(e) => onMaterial(e.target.value)}>
              <option value="">Inventory SKU</option>
              {members.map((member) => (
                <option key={member.material_id} value={member.material_id}>
                  {member.material_code} — {member.material_name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy || !materialId || (bulk ? selectedTags.length === 0 : checked.length === 0)}
              className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
              onClick={onSave}
            >
              Save rule
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}
